import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';

const FeeManagement = () => {
  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [fees, setFees] = useState([]);
  const [masterTypes, setMasterTypes] = useState([]);
  const [participantTypes, setParticipantTypes] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [feeCategories, setFeeCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    participantTypeId: '',
    eventTypeId: '',
    feeCategoryId: '',
    rate: '',
    currency: 'INR',
    validFrom: '',
    validTo: '',
    isActive: true
  });

  // Fetch active conferences on mount
  useEffect(() => {
    fetchActiveConferences();
  }, []);

  // Fetch master types and fees when conference changes
  useEffect(() => {
    if (selectedConferenceId) {
      setParticipantTypes([]);
      setEventTypes([]);
      setFeeCategories([]);
      setFees([]);
      
      // Auto-set currency based on conference country
      const selectedConf = conferences.find(c => c.id === parseInt(selectedConferenceId));
      if (selectedConf?.country) {
        const currency = getCurrencyByCountry(selectedConf.country);
        setFormData(prev => ({ ...prev, currency }));
      }
      
      fetchMasterTypes();
      fetchFees();
    }
  }, [selectedConferenceId, conferences]);

  const fetchActiveConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        setConferences(data);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const fetchMasterTypes = async () => {
    try {
      console.log('Fetching master types for conference:', selectedConferenceId);
      const response = await fetch(`${CONFERENCE_API}/${selectedConferenceId}/master/type`);
      if (response.ok) {
        const types = await response.json();
        console.log('Fetched master types:', types);
        setMasterTypes(types);
        
        // Fetch all details from all master types
        const allDetailsTemp = [];
        
        for (const type of types) {
          const detailsResponse = await fetch(`${CONFERENCE_API}/master/type/${type.id}/details`);
          if (detailsResponse.ok) {
            const details = await detailsResponse.json();
            console.log(`Details for ${type.definition}:`, details);
            
            // Add master type name to each detail for display
            allDetailsTemp.push(...details.map(d => ({ 
              ...d, 
              masterTypeName: type.definition 
            })));
          }
        }
        
        console.log('All master details:', allDetailsTemp);
        
        // Set the same list for all three dropdowns
        setParticipantTypes(allDetailsTemp);
        setEventTypes(allDetailsTemp);
        setFeeCategories(allDetailsTemp);
      }
    } catch (error) {
      console.error('Error fetching master types:', error);
    }
  };

  const fetchFees = async () => {
    setLoading(true);
    try {
      console.log('Fetching fees for conference:', selectedConferenceId);
      const response = await fetch(`${CONFERENCE_API}/fees/conference/${selectedConferenceId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched fees:', data);
        setFees(data);
      }
    } catch (error) {
      console.error('Error fetching fees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert empty strings to null for optional fields
      const payload = {
        participantTypeId: formData.participantTypeId ? parseInt(formData.participantTypeId) : null,
        eventTypeId: formData.eventTypeId ? parseInt(formData.eventTypeId) : null,
        feeCategoryId: formData.feeCategoryId ? parseInt(formData.feeCategoryId) : null,
        conferenceId: parseInt(selectedConferenceId),
        rate: parseFloat(formData.rate),
        currency: formData.currency,
        isActive: formData.isActive
      };

      // Convert date format if provided
      if (formData.validFrom) {
        const [year, month, day] = formData.validFrom.split('-');
        payload.validFrom = `${day}-${month}-${year}`;
      }
      if (formData.validTo) {
        const [year, month, day] = formData.validTo.split('-');
        payload.validTo = `${day}-${month}-${year}`;
      }

      console.log('Creating fee with payload:', payload);
      const response = await fetch(`${CONFERENCE_API}/fees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchFees();
        resetForm();
        setShowCreateModal(false);
        alert('Fee created successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to create fee: ${errorText}`);
      }
    } catch (error) {
      console.error('Error creating fee:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      // Convert empty strings to null for optional fields
      const payload = {
        participantTypeId: formData.participantTypeId ? parseInt(formData.participantTypeId) : null,
        eventTypeId: formData.eventTypeId ? parseInt(formData.eventTypeId) : null,
        feeCategoryId: formData.feeCategoryId ? parseInt(formData.feeCategoryId) : null,
        conferenceId: parseInt(selectedConferenceId),
        rate: parseFloat(formData.rate),
        currency: formData.currency,
        isActive: formData.isActive
      };

      // Convert date format if provided
      if (formData.validFrom) {
        const [year, month, day] = formData.validFrom.split('-');
        payload.validFrom = `${day}-${month}-${year}`;
      }
      if (formData.validTo) {
        const [year, month, day] = formData.validTo.split('-');
        payload.validTo = `${day}-${month}-${year}`;
      }

      const response = await fetch(`${CONFERENCE_API}/fees/${editingFee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchFees();
        resetForm();
        setShowEditModal(false);
        setEditingFee(null);
        alert('Fee updated successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to update fee: ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating fee:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleEdit = (fee) => {
    console.log('Editing fee:', fee);
    console.log('ValidFrom:', fee.validFrom, 'ValidTo:', fee.validTo);
    
    setEditingFee(fee);
    
    // Convert dates to yyyy-MM-dd format for input
    let validFrom = '';
    let validTo = '';
    
    if (fee.validFrom && fee.validFrom !== '-' && typeof fee.validFrom === 'string' && fee.validFrom.trim() !== '') {
      const parts = fee.validFrom.split('-');
      if (parts.length === 3) {
        // Check if it's already in yyyy-MM-dd format (year is first and 4 digits)
        if (parts[0].length === 4) {
          validFrom = fee.validFrom;
        } else {
          // It's in dd-MM-yyyy format, convert it
          const [day, month, year] = parts;
          validFrom = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
    }
    if (fee.validTo && fee.validTo !== '-' && typeof fee.validTo === 'string' && fee.validTo.trim() !== '') {
      const parts = fee.validTo.split('-');
      if (parts.length === 3) {
        // Check if it's already in yyyy-MM-dd format (year is first and 4 digits)
        if (parts[0].length === 4) {
          validTo = fee.validTo;
        } else {
          // It's in dd-MM-yyyy format, convert it
          const [day, month, year] = parts;
          validTo = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
    }

    console.log('Converted validFrom:', validFrom, 'validTo:', validTo);

    setFormData({
      participantTypeId: fee.participantType?.id || '',
      eventTypeId: fee.eventType?.id || '',
      feeCategoryId: fee.feeCategory?.id || '',
      rate: fee.rate || '',
      currency: fee.currency || getCurrencyByCountry(conferences.find(c => c.id === parseInt(selectedConferenceId))?.country),
      validFrom: validFrom,
      validTo: validTo,
      isActive: fee.isActive
    });
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fee?')) return;

    try {
      const response = await fetch(`${CONFERENCE_API}/fees/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchFees();
        alert('Fee deleted successfully!');
      } else {
        const errorText = await response.text();
        alert(`Failed to delete fee: ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting fee:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const getCurrencyByCountry = (country) => {
    if (!country) return 'USD';
    
    const countryLower = country.toLowerCase().trim();
    
    // Currency mapping by country
    const currencyMap = {
      // Asia-Pacific
      'india': 'INR',
      'australia': 'AUD',
      'china': 'CNY',
      'japan': 'JPY',
      'south korea': 'KRW',
      'singapore': 'SGD',
      'malaysia': 'MYR',
      'thailand': 'THB',
      'indonesia': 'IDR',
      'philippines': 'PHP',
      'vietnam': 'VND',
      'new zealand': 'NZD',
      'hong kong': 'HKD',
      'taiwan': 'TWD',
      'pakistan': 'PKR',
      'bangladesh': 'BDT',
      'sri lanka': 'LKR',
      
      // Europe (Eurozone)
      'austria': 'EUR',
      'belgium': 'EUR',
      'cyprus': 'EUR',
      'estonia': 'EUR',
      'finland': 'EUR',
      'france': 'EUR',
      'germany': 'EUR',
      'greece': 'EUR',
      'ireland': 'EUR',
      'italy': 'EUR',
      'latvia': 'EUR',
      'lithuania': 'EUR',
      'luxembourg': 'EUR',
      'malta': 'EUR',
      'netherlands': 'EUR',
      'portugal': 'EUR',
      'slovakia': 'EUR',
      'slovenia': 'EUR',
      'spain': 'EUR',
      
      // Europe (Non-Eurozone)
      'united kingdom': 'GBP',
      'uk': 'GBP',
      'switzerland': 'CHF',
      'norway': 'NOK',
      'sweden': 'SEK',
      'denmark': 'DKK',
      'poland': 'PLN',
      'czech republic': 'CZK',
      'hungary': 'HUF',
      'romania': 'RON',
      'bulgaria': 'BGN',
      'croatia': 'EUR',
      'iceland': 'ISK',
      'russia': 'RUB',
      'turkey': 'TRY',
      'ukraine': 'UAH',
      
      // Americas
      'united states': 'USD',
      'usa': 'USD',
      'us': 'USD',
      'canada': 'CAD',
      'mexico': 'MXN',
      'brazil': 'BRL',
      'argentina': 'ARS',
      'chile': 'CLP',
      'colombia': 'COP',
      'peru': 'PEN',
      
      // Middle East & Africa
      'united arab emirates': 'AED',
      'uae': 'AED',
      'saudi arabia': 'SAR',
      'israel': 'ILS',
      'qatar': 'QAR',
      'kuwait': 'KWD',
      'south africa': 'ZAR',
      'egypt': 'EGP',
      'nigeria': 'NGN',
      'kenya': 'KES'
    };
    
    return currencyMap[countryLower] || 'USD';
  };

  const resetForm = () => {
    // Preserve currency from selected conference
    const selectedConf = conferences.find(c => c.id === parseInt(selectedConferenceId));
    const currency = getCurrencyByCountry(selectedConf?.country);
    
    setFormData({
      participantTypeId: '',
      eventTypeId: '',
      feeCategoryId: '',
      rate: '',
      currency: currency,
      validFrom: '',
      validTo: '',
      isActive: true
    });
  };

  const renderFeeForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
          Participant Type (Category)
        </label>
        <select
          value={formData.participantTypeId}
          onChange={(e) => setFormData({ ...formData, participantTypeId: e.target.value })}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">Select Participant Type</option>
          {participantTypes.map(type => (
            <option key={type.id} value={type.id}>
              {type.value} ({type.masterTypeName})
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
          Registration Type (Event Type)
        </label>
        <select
          value={formData.eventTypeId}
          onChange={(e) => setFormData({ ...formData, eventTypeId: e.target.value })}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">Select Registration Type</option>
          {eventTypes.map(type => (
            <option key={type.id} value={type.id}>
              {type.value} ({type.masterTypeName})
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
          Fee Category (optional)
        </label>
        <select
          value={formData.feeCategoryId}
          onChange={(e) => setFormData({ ...formData, feeCategoryId: e.target.value })}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">Select Fee Category</option>
          {feeCategories.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.value} ({cat.masterTypeName})
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '15px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
            Rate *
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.rate}
            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
            Currency *
          </label>
          <input
            type="text"
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
            required
            placeholder="e.g., INR, USD"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
            Valid From
          </label>
          <input
            type="date"
            value={formData.validFrom}
            onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
            Valid To
          </label>
          <input
            type="date"
            value={formData.validTo}
            onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            style={{ marginRight: '8px' }}
          />
          <span style={{ fontWeight: '500' }}>Active</span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowCreateModal(false);
            setShowEditModal(false);
            setEditingFee(null);
          }}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {submitLabel}
        </button>
      </div>
    </form>
  );

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Fee Management</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Conference:</label>
            <select
              value={selectedConferenceId}
              onChange={(e) => setSelectedConferenceId(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '14px',
                minWidth: '250px'
              }}
            >
              <option value="">All Conferences</option>
              {conferences.map(conf => (
                <option key={conf.id} value={conf.id}>{conf.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
            disabled={!selectedConferenceId}
          >
            ➕ Add New Fee
          </button>
        </div>
      </div>

      {loading && <div>Loading fees...</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>Participant Type</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Registration Type</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Fee Category</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Rate</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Currency</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Valid From</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Valid To</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fees.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                  No fees configured for this conference
                </td>
              </tr>
            ) : (
              fees.map((fee) => (
                <tr key={fee.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{fee.participantType?.value || '-'}</td>
                  <td style={{ padding: '12px' }}>{fee.eventType?.value || '-'}</td>
                  <td style={{ padding: '12px' }}>{fee.feeCategory?.value || '-'}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                    {fee.rate?.toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{fee.currency}</td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>
                    {fee.validFrom || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>
                    {fee.validTo || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: fee.isActive ? '#d4edda' : '#f8d7da',
                      color: fee.isActive ? '#155724' : '#721c24'
                    }}>
                      {fee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEdit(fee)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(fee.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Fee Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '600px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Add New Fee</h3>
            {renderFeeForm(handleSubmit, 'Create Fee')}
          </div>
        </div>
      )}

      {/* Edit Fee Modal */}
      {showEditModal && editingFee && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '600px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Edit Fee</h3>
            {renderFeeForm(handleUpdate, 'Update Fee')}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeManagement;
